import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { resolveImage } from "@/lib/api";

const sizeClasses = {
  large: "md:col-span-2 md:row-span-2 min-h-[280px] md:min-h-[560px]",
  tall: "md:col-span-1 md:row-span-2 min-h-[280px] md:min-h-[560px]",
  wide: "md:col-span-2 md:row-span-1 min-h-[280px]",
  square: "md:col-span-1 md:row-span-1 min-h-[280px]",
};

const CategoryCard = ({ cat, index, onCategory }) => {
  const ref = useRef(null);

  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.article
      ref={ref}
      onMouseMove={handleMove}
      onClick={() => onCategory(cat.id)}
      data-testid={`category-${cat.id}`}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.76, 0, 0.24, 1] }}
      className={`group relative overflow-hidden bg-ad-surface border border-white/10 cursor-pointer ${sizeClasses[cat.size] || sizeClasses.square}`}
    >
      <img
        src={resolveImage(cat.image_url)}
        alt={cat.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
      {/* spotlight */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background: "radial-gradient(400px circle at var(--mx) var(--my), rgba(230,0,0,0.25), transparent 60%)",
        }}
      />

      {cat.badge && (
        <span className="absolute top-4 left-4 z-10 bg-ad-red text-white text-[11px] font-bold uppercase tracking-widest px-3 py-1.5">
          {cat.badge}
        </span>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8 z-10">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl sm:text-4xl uppercase leading-none tracking-tight text-white">
              {cat.name}
            </h3>
            <p className="mt-3 text-white/70 text-sm sm:text-base max-w-md leading-relaxed">{cat.description}</p>
          </div>
          <span className="shrink-0 h-11 w-11 grid place-items-center border border-white/30 group-hover:bg-ad-red group-hover:border-ad-red transition-colors">
            <ArrowUpRight size={20} className="text-white" />
          </span>
        </div>
      </div>
    </motion.article>
  );
};

export const Categories = ({ categories, onCategory }) => {
  return (
    <section id="collections" className="relative py-24 sm:py-32 px-5 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6"
        >
          <div>
            <p className="text-ad-red text-xs sm:text-sm font-bold uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-ad-red" /> Our Collections
            </p>
            <h2 className="font-display text-5xl sm:text-7xl uppercase leading-[0.9] tracking-tight">
              Shop Every <span className="metallic-text">Room</span>
            </h2>
          </div>
          <p className="max-w-sm text-white/60 leading-relaxed">
            From clearance mattresses to complete living room sets — find everything your home needs under one roof.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-4 auto-rows-auto gap-4">
          {categories.map((cat, i) => (
            <CategoryCard key={cat.id} cat={cat} index={i} onCategory={onCategory} />
          ))}
        </div>
      </div>
    </section>
  );
};
